import { Message } from '../ChatWidget.interfaces';

/**
 * Chatbase message format
 */
export interface ChatbaseMessage {
  content: string;
  role: 'user' | 'assistant';
}

/**
 * Convert internal messages to Chatbase format
 */
export function toChatbaseMessages(messages: Message[]): ChatbaseMessage[] {
  return messages
    .filter((msg) => msg.text.trim())
    .map((msg) => ({
      content: msg.text,
      role: msg.role === 'bot' ? ('assistant' as const) : ('user' as const),
    }));
}

/**
 * Stream chat response from server API
 * Returns an async generator that yields text chunks as they arrive
 */
export async function* streamChatResponse(
  messages: ChatbaseMessage[],
  conversationId: string,
): AsyncGenerator<string, void, unknown> {
  const response = await fetch('/api/chatbase/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ messages, conversationId }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Chat API error: ${response.status} - ${errorText}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      yield chunk;
    }
  } finally {
    reader.releaseLock();
  }
}
